import _ from 'lodash'
import Backbone from 'backbone'
import Nav from 'scoreboard/components/nav'
import conductor from 'scoreboard/components/conductor'
import globals from 'scoreboard/components/globals'
import appDisplay from 'scoreboard/components/app-display'
import permissions from 'scoreboard/components/permissions'
import WizardInfo from './wizard/reports-wizard-info'
import Report from './report'
import Wizard from './wizard/reports-wizard'
import Overview from './reports-overview'
import guard from 'scoreboard/components/unsaved/guard'
import Edit from './reports-edit'
import user from 'scoreboard/components/user'
import reportConstants from './report-constants'
import CustomIconGardener from 'scoreboard/components/custom-icon-gardener'

var ReportsNav = Nav.extend({

	// see #13646 for reason why we don't want to disable the add report button
	disableAddOnNew: false,

	Model: Report,

	folders: true,

	section: 'reports',

	Gardener: CustomIconGardener,

	preventGuardOnClickEdit: true,

	events: _.extend({}, Nav.prototype.events, {
		'click .create-first': 'create',
		'click .new': 'create'
	}),

	// make sure transient nodes are assigned the correct icon
	_defaultTransient: function(attributes) {
		// #39816 make sure we also have the correct icon if this is a folder
		let icon = attributes.type === 'folder' ? 'folder' : 'reports'
		return _.assign(Nav.prototype._defaultTransient.apply(this, arguments), {
			icon
		})
	},

	// renders the new report dialog
	create: function(e) {
		guard.conditionallyRun(function() {
			//#13646, remove any previous unsaved transient otherwise the sort index will be wrong
			this.removeTransient()

			//new reports don't have an id until saved, so any url id references for
			//nav purposes will be in reference to the last loaded report
			var relId = conductor.id() || this.options.id
			if (_.isFinite(relId)) {
				this.prevReportId = relId
			}

			var wizard = new Wizard({ model: new WizardInfo() })
			appDisplay.dialog(wizard)
			wizard.once('done', function(report) {
				let isDesigner = !!report.designer

				//#8573 allow reports to be saved under folders
				var display = appDisplay.displayView() || {}
				var displayModel = display.model
				if (displayModel && displayModel.get('type') === 'folder') {
					report.set('parentId', relId, {silent: true})
				}

				//#18702 when creating under a replicated item, send to root level
				if (displayModel && !_.isNil(displayModel.get('syncId'))) {
					relId = null
					report.set('parentId', relId, {silent: true})
				}

				//this would be null if coming from organizations/343/reports
				if (this.prevReportId) {
					//store id of report that initiated new report #9102
					report.prevReportId = this.prevReportId
				}

				let fetchOpts = isDesigner ? reportConstants.EDIT_FETCH_OPTIONS : {}
				appDisplay.display(report.fetch(fetchOpts), () => {
					if (!relId) {
						//then we're in the first report in the org
						conductor.navigate('organizations/' + globals.orgId + '/' + this.section + '/new')
					} else {
						//update to report url (but don't save in history)
						let url = 'reports/new'
						if (isDesigner) url += '/edit'
						conductor.navigate(url, {replace:true})
					}

					//the add transient method is smart enough to use the current
					//transient if it exists
					this.addTransient(relId, report)
					report.on('change', () => this.changeTransient(report.attributes))
					report.on('sync', () => {
						this.saveTransient(relId, report.attributes)
						//make sure the newly saved object is the one that is selected (cannot select a transient)
						this.gardener.tree.select(report.id)
					})

					this.toggleEditButton(isDesigner)
					this.toggleEditViewState(isDesigner)
					let view = isDesigner ? Edit : Overview
					return new view({model: report})
				})
			}.bind(this))
		}.bind(this))
	},

	isEditable: function(orgId) {
		return !user.isStrictViewer()	//#9287
	},

	addFolder: function () {
		guard.conditionallyRun(function() {
			let id = conductor.id() || this.prevReportId
			let idParam = id ? '/'+ id  : ''
			conductor.navigate(this.section + idParam + '/new-folder', { trigger: true })
		}.bind(this))
	},

	toggleEdit: function (edit) {
		this.toggleEditViewState(edit)

		//#25089 if path has organization, we're no longer looking at a
		//previous saved report
		if (Backbone.history.fragment.indexOf('organizations/') != -1) {
			delete this.prevReportId
		}

		var hasId = _.isFinite(conductor.id())
		const isNew = Backbone.history.fragment.indexOf('/new') != -1
		if (hasId && !isNew) {
			Nav.prototype.toggleEdit.call(this, edit)
		} else {
			// handle triggering edit/overview view when first unsaved report in empty org
			if (Backbone.history.fragment
				&& Backbone.history.fragment.indexOf('organizations/') != -1
				&& isNew) {
				var url = 'organizations/' + globals.orgId + '/' + this.section + '/new'
				url += edit ? '/edit' : ''
				conductor.navigate(url, {trigger: true})
			} else if (isNew) {
				const isEdit = Backbone.history.fragment.indexOf('/edit') != -1
				let url = 'reports/new'

				// isEdit indicates whether we are on the edit tab
				// edit indicates whether we are navigating to the edit tab
				if (isEdit && !edit) {
					conductor.navigate(url, {trigger: true})
				} else if (!isEdit && edit) {
					url += '/edit'
					conductor.navigate(url, {trigger: true})
				}
			}
		}
		return edit
	},

	_addDomainObject: function(model, relativeId) {
		var relId = model.get('parentId') || relativeId

		if (model.get('type') === 'report') {
			if (this._wrongOrg(model)) {
				return
			}

			this.toggleTree(true)

			if (this.gardener.tree.getTransient()) {
				this.saveTransient(relId, model)
			} else {
				this.gardener.add(model, relId)
			}
			this.gardener.tree.select(model.id, { silent: true })

			if (Backbone.history.fragment.indexOf('/edit') != -1) {
				conductor.edit(this.section, model.id)
			} else {
				conductor.overview(this.section, model.id)
			}
		} else {
			Nav.prototype._addDomainObject.call(this, model, relId)
		}
	},

	//unlike other sections, you can continue to save the changes on a report
	//on the overview screen so when toggling between edit modes don't apply guard.
	//In scenarios where the guard should toggled because of a route change, an
	//appropriate guard still be shown by the routers, as the guard defined
	//in the toolbar only handles guarding due to the toggling of the edit mode
	toggleEditButton: function(isEditing) {
		this._toggleEditButton.apply(this, arguments)
	},

	onSelect (node) {
		let currentModel = _.get(appDisplay.displayView(), 'model')
		if (currentModel && currentModel.isNew()) {
			//#22862 unsaved reports don't change the url because there is no id associated with them
			//in this case once get unsaved guard resolves, #37007 remove the old
			//view to clean up properly
			guard.conditionallyRun(() => {
				appDisplay.displayView().remove()
				Nav.prototype.onSelect.apply(this, arguments)
			})
		} else {
			Nav.prototype.onSelect.apply(this, arguments)
		}
	},

	secure: function() {
		Nav.prototype.secure.call(this, arguments)

		//#25588 the first hit requires data from the server that will only
		//happen after the nav has rendered, so to do #9287 we need to hide the
		//add folder here
		if (!permissions.reports(this.gardener.getOrgId())) {
			this._toggleFolderButton(false)
		}
	},

	toggleEditability: function(editable) {
		//#9287 allows the edit tab to be visitable pushing this into edit mode
		//but viewers aren't actually allow to edit the tree so check to make
		//sure the user has modify report permission before actually entering
		//edit mode on nav
		Nav.prototype.toggleEditability.call(this, editable &&
			permissions.reports(this.gardener.getOrgId()))
	},

	toggleEditViewState: function(edit) {
		// #40928 Nav not in edit mode after refreshing Report edit tab
		let editing = edit || _.endsWith(Backbone.history.fragment, '/edit')

		//see above
		Nav.prototype.toggleEditViewState.call(this, editing &&
			permissions.reports(this.gardener.getOrgId()))
	}


})

export default ReportsNav

ReportsNav.__filename__ = 'reports/reports-nav.js'
